import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { UserRole, RolePermissions, ROLE_PERMISSIONS } from '@/types/team';

interface TeamMemberInfo {
  is_admin?: boolean;
  role?: string;
  siteOfficeId?: string;
}

// Read team member from localStorage to avoid circular dependency with AuthContext
const getTeamMemberFromStorage = (): TeamMemberInfo | null => {
  try {
    const stored = localStorage.getItem('currentUser');
    if (stored) {
      return JSON.parse(stored);
    }
  } catch {}
  return null;
};

interface UserRoleContextType {
  currentRole: UserRole;
  permissions: RolePermissions;
  setUserRole: (role: UserRole) => void;
  hasPermission: (permission: keyof RolePermissions) => boolean;
  canAccessMenu: (menuId: string) => boolean;
  isSetupMode: boolean;
  canViewAllSites: boolean;
  isSiteRestricted: boolean;
  canPrint: boolean;
}

const UserRoleContext = createContext<UserRoleContextType | undefined>(undefined);

interface UserRoleProviderProps {
  children: ReactNode;
}

export const UserRoleProvider: React.FC<UserRoleProviderProps> = ({ children }) => {
  const [currentRole, setCurrentRole] = useState<UserRole>('viewer');
  const [isSetupMode, setIsSetupMode] = useState(false);
  const [teamMember, setTeamMember] = useState<TeamMemberInfo | null>(getTeamMemberFromStorage());

  // Force administrator role in setup mode
  const effectiveRole = isSetupMode ? 'administrator' : currentRole;
  const permissions = ROLE_PERMISSIONS[effectiveRole];

  useEffect(() => {
    // Check setup mode status and sync teamMember from localStorage
    const checkState = () => {
      const setupModeActive = localStorage.getItem('setupMode') === 'true';
      setIsSetupMode(setupModeActive);

      // Sync teamMember from localStorage
      const storedMember = getTeamMemberFromStorage();
      setTeamMember(storedMember);
      
      if (setupModeActive) {
        return;
      }

      // Normal role logic when not in setup mode
      if (storedMember) {
        const authRole = localStorage.getItem('userRole') as UserRole;
        if (authRole && Object.keys(ROLE_PERMISSIONS).includes(authRole)) {
          setCurrentRole(authRole);
        } else {
          setCurrentRole('viewer');
        }
      } else {
        setCurrentRole('viewer');
      }
    };

    checkState();

    // Listen for storage changes from other tabs
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'setupMode' || e.key === 'userRole' || e.key === 'currentUser') {
        checkState();
      }
    };

    // Listen for same-tab custom events (dispatched by AuthContext)
    const handleUserChanged = () => checkState();

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('userRoleChanged', handleUserChanged);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('userRoleChanged', handleUserChanged);
    };
  }, []);

  const changeRole = (newRole: UserRole) => {
    if (isSetupMode) {
      console.log('🔧 Role changes disabled in setup mode - maintaining administrator access');
      return;
    }

    setCurrentRole(newRole);
    localStorage.setItem('userRole', newRole);
  };

  const hasPermission = (permission: keyof RolePermissions): boolean => {
    // In setup mode, always return true for administrator permissions
    if (isSetupMode) {
      return ROLE_PERMISSIONS.administrator[permission];
    }
    return permissions[permission];
  };

  const canAccessMenu = (menuId: string): boolean => {
    // In setup mode, grant access to all menus
    if (isSetupMode) {
      return true;
    }

    // Check if user is admin for admin-specific menus
    if (teamMember?.is_admin || teamMember?.role === 'administrator') {
      if (menuId === 'admin' || menuId === 'admin-chat' || menuId === 'admin-members' || menuId.startsWith('admin-')) {
        return true;
      }
    }

    // Site admin can access all menus except global admin panel
    if (teamMember?.role === 'site-admin') {
      if (menuId === 'admin' || menuId.startsWith('admin-')) {
        return false;
      }
      return true;
    }

    // Management can access all view menus
    if (teamMember?.role === 'management') {
      if (menuId === 'admin' || menuId.startsWith('admin-')) {
        return false;
      }
      return true;
    }

    // Document-budget-editor can access budget menu
    if (teamMember?.role === 'document-budget-editor') {
      if (menuId === 'budget' || menuId === 'documents') {
        return true;
      }
    }

    switch (menuId) {
      case 'overview':
        return permissions.canViewOverview;
      case 'dashboard':
        return permissions.canViewDashboard;
      case 'projects':
        return permissions.canViewProjects;
      case 'schedule':
        return permissions.canViewSchedule;
      case 'budget':
        return permissions.canViewBudgetRAB;
      case 'documents':
        return permissions.canViewDocuments;
      case 'teams':
        return permissions.canViewTeams;
      case 'reports':
        return permissions.canViewReports;
      case 'settings':
        return permissions.canViewSettings;
      case 'safety':
      case 'wh-ga': // Navigation only; logistics enforces its own authentication and approval.
        return true;
      case 'marketing': {
        const mRole = teamMember?.role as string | undefined;
        if (teamMember?.is_admin || mRole === 'administrator' || mRole === 'site-admin' || mRole === 'management') {
          return true;
        }
        return false;
      }
      case 'planning': {
        // Planning accessible to admin, site-admin, management
        const pRole = teamMember?.role as string | undefined;
        if (teamMember?.is_admin || pRole === 'administrator' || pRole === 'site-admin' || pRole === 'management') {
          return true;
        }
        return false;
      }
      case 'announcements': {
        // Only administrator, site-admin, and management can access
        const role = teamMember?.role as string | undefined;
        if (teamMember?.is_admin || role === 'administrator' || role === 'site-admin' || role === 'management') {
          return true;
        }
        return false;
      }
      case 'info':
        return true;
      case 'help':
        return true;
      default:
        return false;
    }
  };

  const value: UserRoleContextType = {
    currentRole: effectiveRole,
    permissions,
    setUserRole: changeRole,
    hasPermission,
    canAccessMenu,
    isSetupMode,
    canViewAllSites: permissions.canViewAllSites,
    isSiteRestricted: permissions.isSiteRestricted,
    canPrint: permissions.canPrintReports,
  };

  return (
    <UserRoleContext.Provider value={value}>
      {children}
    </UserRoleContext.Provider>
  );
};

export const useUserRole = (): UserRoleContextType => {
  const context = useContext(UserRoleContext);
  if (context === undefined) {
    throw new Error('useUserRole must be used within a UserRoleProvider');
  }
  return context;
}; 
